/**
 * Visão geral do financeiro — os KPIs de `GET /financeiro/resumo`.
 *
 * A pergunta desta aba é uma só: "o que eu preciso fazer com dinheiro hoje?".
 * Por isso cada indicador que representa trabalho é CLICÁVEL e leva para a
 * aba com o recorte já aplicado — um número que não muda decisão nenhuma não
 * merece a dobra da tela.
 *
 * O que a spec 03 lista e ainda não existe (fluxo de caixa, conciliação, DRE,
 * comissão) NÃO vira aba vazia. Uma aba que abre e não faz nada é pior que aba
 * nenhuma: ensina que a tela está quebrada. Fica um bloco no fim dizendo o que
 * falta e por quê — o mesmo princípio do Dashboard.
 */
import { useEffect, useState } from 'react'

import { Icone, type NomeIcone } from '../../componentes/Icone'
import { CartaoKPI } from '../../componentes/Listagem'
import { ErroApi } from '../../plataforma/api'
import { formatarMoeda } from '../../plataforma/dinheiro'
import {
  carregarResumoFinanceiro,
  type Lado,
  type RecorteDeContas,
  type ResumoFinanceiro,
} from '../../plataforma/financeiro'

/** O que a spec pede e o módulo ainda não entrega. */
const AINDA_NAO_EXISTE: { titulo: string; falta: string; icone: NomeIcone }[] = [
  {
    titulo: 'Fluxo de caixa',
    icone: 'bar_chart',
    falta:
      'Depende de saldo bancário e da projeção de venda pela média das 4 semanas anteriores. Nem o saldo de conta nem o turno de caixa existem na API — sem eles, a projeção seria um desenho.',
  },
  {
    titulo: 'Conciliação de cartão',
    icone: 'percent',
    falta:
      'Depende do extrato da adquirente e da taxa contratada por bandeira. Não há integração com adquirente nem cadastro de taxa.',
  },
  {
    titulo: 'DRE',
    icone: 'trending_up',
    falta:
      'Depende de plano de contas e de classificação de despesa. A conta a pagar de hoje não tem categoria — sem ela, todo custo cairia na mesma linha.',
  },
  {
    titulo: 'Comissão',
    icone: 'group',
    falta:
      'Depende de percentual por vendedor e de meta mensal, que ainda não existem no cadastro de usuário.',
  },
]

export function VisaoGeral({
  aoAbrir,
}: {
  /** Leva para a aba do lado escolhido, com o recorte já aplicado. */
  aoAbrir: (lado: Lado, recorte: RecorteDeContas) => void
}) {
  const [resumo, setResumo] = useState<ResumoFinanceiro | null>(null)
  const [erro, setErro] = useState('')
  const [versao, setVersao] = useState(0)

  useEffect(() => {
    const controle = new AbortController()
    carregarResumoFinanceiro(controle.signal)
      .then((dados) => {
        setResumo(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(
          causa instanceof ErroApi
            ? causa.message
            : 'Não foi possível carregar o resumo do financeiro.',
        )
      })
    return () => controle.abort()
  }, [versao])

  if (erro) {
    return (
      <div
        role="alert"
        className="flex items-center gap-3 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto"
      >
        <Icone nome="error" tamanho={18} className="shrink-0" />
        <span className="min-w-0 flex-1">{erro}</span>
        <button
          type="button"
          onClick={() => setVersao((n) => n + 1)}
          className="h-8 shrink-0 rounded-sistema border border-erro bg-superficie px-3 text-[12.5px] font-semibold text-erro"
        >
          Tentar de novo
        </button>
      </div>
    )
  }

  if (!resumo) {
    return <div className="h-[220px] animate-pulse rounded-sistema bg-superficie" />
  }

  const receberAtrasadas = resumo.contas_a_receber_atrasadas
  const pagarAtrasadas = resumo.contas_a_pagar_atrasadas

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Atrasado é ERRO, vence hoje é ATENÇÃO, quitado é neutro — cor só onde
          alguém precisa agir hoje (spec 01). */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <CartaoKPI
          icone="warning"
          tom={resumo.a_receber_atrasado_centavos > 0 ? 'erro' : 'neutro'}
          rotulo="A receber atrasado"
          valor={formatarMoeda(resumo.a_receber_atrasado_centavos)}
          apoio={
            resumo.a_receber_atrasado_centavos > 0
              ? contarOu(receberAtrasadas, 'conta vencida', 'contas vencidas', 'cobrança em atraso')
              : 'ninguém em atraso'
          }
          aoClicar={() => aoAbrir('receber', 'atrasadas')}
        />
        <CartaoKPI
          icone="calendario"
          tom={resumo.a_receber_hoje_centavos > 0 ? 'atencao' : 'neutro'}
          rotulo="A receber hoje"
          valor={formatarMoeda(resumo.a_receber_hoje_centavos)}
          apoio={resumo.a_receber_hoje_centavos > 0 ? 'vence hoje' : 'nada vence hoje'}
          aoClicar={() => aoAbrir('receber', 'vence_hoje')}
        />
        <CartaoKPI
          icone="warning"
          tom={resumo.a_pagar_atrasado_centavos > 0 ? 'erro' : 'neutro'}
          rotulo="A pagar atrasado"
          valor={formatarMoeda(resumo.a_pagar_atrasado_centavos)}
          apoio={
            resumo.a_pagar_atrasado_centavos > 0
              ? contarOu(pagarAtrasadas, 'conta vencida', 'contas vencidas', 'despesa em atraso')
              : 'nada vencido'
          }
          aoClicar={() => aoAbrir('pagar', 'atrasadas')}
        />
        <CartaoKPI
          icone="calendario"
          tom={resumo.a_pagar_hoje_centavos > 0 ? 'atencao' : 'neutro'}
          rotulo="A pagar hoje"
          valor={formatarMoeda(resumo.a_pagar_hoje_centavos)}
          apoio={resumo.a_pagar_hoje_centavos > 0 ? 'vence hoje' : 'nada vence hoje'}
          aoClicar={() => aoAbrir('pagar', 'vence_hoje')}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <CartaoKPI
          icone="check_circle"
          rotulo="Recebido no mês"
          valor={formatarMoeda(resumo.recebido_no_mes_centavos)}
          apoio="o que já entrou"
          aoClicar={() => aoAbrir('receber', 'baixadas_no_mes')}
        />
        <CartaoKPI
          icone="check_circle"
          rotulo="Pago no mês"
          valor={formatarMoeda(resumo.pago_no_mes_centavos)}
          apoio="o que já saiu"
          aoClicar={() => aoAbrir('pagar', 'baixadas_no_mes')}
        />
        {/* Saldo projetado não recorta lista nenhuma — é resultado, não filtro.
            Fica sem clique, e vermelho só quando for negativo: aí sim alguém
            precisa agir hoje. */}
        <CartaoKPI
          icone="payments"
          tom={resumo.saldo_projetado_centavos < 0 ? 'erro' : 'neutro'}
          rotulo="Saldo projetado do mês"
          valor={formatarMoeda(resumo.saldo_projetado_centavos)}
          apoio={
            resumo.saldo_projetado_centavos < 0
              ? 'o mês fecha no vermelho se nada mudar'
              : 'recebido e a receber, menos pago e a pagar'
          }
        />
      </div>

      <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
        <h2 className="font-titulo text-base font-semibold text-tinta">
          O que o financeiro ainda não faz
        </h2>
        <p className="mt-[3px] text-[12.5px] text-texto-suave">
          Estas quatro visões estão na especificação e não têm dado por trás hoje. Elas não
          aparecem como aba vazia de propósito — aba que abre e não faz nada ensina que a tela
          está quebrada.
        </p>

        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {AINDA_NAO_EXISTE.map((item) => (
            <li
              key={item.titulo}
              className="flex gap-3 rounded-sistema border border-divisoria bg-fundo px-4 py-3"
            >
              <Icone nome={item.icone} tamanho={18} className="mt-0.5 shrink-0 text-inativo" />
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-texto">{item.titulo}</p>
                <p className="mt-1 text-[12.5px] leading-[1.5] text-texto-suave">{item.falta}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

/**
 * "3 contas vencidas" quando o servidor mandou a contagem; o texto genérico
 * quando não mandou. Contagem é apoio ao número, não o número — inventar "0
 * contas" ao lado de um valor maior que zero seria contradição na mesma linha.
 */
function contarOu(
  quantas: number | undefined,
  singular: string,
  plural: string,
  semContagem: string,
): string {
  if (quantas === undefined) return semContagem
  return `${quantas} ${quantas === 1 ? singular : plural}`
}
